import React from 'react';
import '../styles/Timeline.css';

interface TimelineEvent {
  year: string;
  title: string;
  description: string;
  type: 'milestone' | 'achievement' | 'decline' | 'revival';
  link?: { url: string; label: string };
}

const events: TimelineEvent[] = [
  {
    year: '2012',
    title: 'Jibo, Inc. Founded',
    description: 'Cynthia Breazeal and Steve Chambers co-founded Jibo, Inc. in Boston, bringing decades of social robotics research from MIT to a consumer product.',
    type: 'milestone',
  },
  {
    year: 'Jul 2014',
    title: 'Indiegogo Campaign Launch',
    description: 'Jibo launched on Indiegogo with a $100,000 goal. It reached that in under 4 hours, crossed $1 million in 6 days, and ultimately raised over $3.7 million from 7,422 backers. Jibo earned a Guinness World Record for Most Crowdfunded Social Robot.',
    type: 'achievement',
    link: { url: 'https://www.indiegogo.com/projects/jibo-the-world-s-first-social-robot-for-the-home', label: 'View Indiegogo Campaign' },
  },
  {
    year: '2015',
    title: '$37.4M Series A & CES Debut',
    description: 'Major venture capital funding from CRV, Flybridge Capital Partners, Acer, and others. Total funding would eventually reach approximately $73 million. Jibo was showcased at CES 2015 as one of the most intriguing robots on display.',
    type: 'milestone',
  },
  {
    year: 'Dec 2015',
    title: 'Harvard Business School Case Study',
    description: 'Harvard Business School published "Jibo: A Social Robot for the Home" as a teaching case, analyzing the company\'s business strategy and developer ecosystem decisions.',
    type: 'achievement',
    link: { url: 'https://www.hbs.edu/faculty/Pages/item.aspx?num=50236', label: 'View HBS Case' },
  },
  {
    year: 'May 2016',
    title: 'Jibo SDK Released',
    description: 'Jibo launched its beta SDK, enabling third-party developers to build "Jibo Skills" with animation editors, JavaScript APIs, and a virtual simulator.',
    type: 'milestone',
  },
  {
    year: 'Aug 2016',
    title: 'International Orders Cancelled',
    description: 'Jibo cancelled all orders outside the US and Canada, issuing refunds to backers in 45 countries. The company cited server latency, accent recognition challenges, and international privacy regulations.',
    type: 'decline',
    link: { url: 'https://techcrunch.com/2016/08/11/uh-oh-crowdfunded-social-robot-jibo-wont-now-ship-internationally/', label: 'Read on TechCrunch' },
  },
  {
    year: 'Sep 2017',
    title: 'First Robots Ship',
    description: 'After three years of development and delays, the first 3,000 Jibo robots shipped to Indiegogo backers. The consumer version launched at $899.',
    type: 'milestone',
  },
  {
    year: 'Nov 2017',
    title: 'TIME\'s Best Inventions',
    description: 'TIME Magazine named Jibo one of the "25 Best Inventions of 2017," placing it at the top of the list and featuring it on the cover. Also a CES 2018 Innovation Awards Honoree.',
    type: 'achievement',
  },
  {
    year: 'Nov 2018',
    title: 'Jibo, Inc. Closes',
    description: 'After struggling with market fit and funding, Jibo laid off most staff and sold its assets to SQN Venture Partners. The dream of social robots in every home was fading.',
    type: 'decline',
  },
  {
    year: 'Mar 2019',
    title: 'The Goodbye',
    description: 'Jibo delivered an emotional farewell message to owners before its servers shut down. The viral video was covered by TechCrunch, Engadget, Boing Boing, Gizmodo, and more, becoming a cultural moment about what it means to bond with machines.',
    type: 'decline',
    link: { url: 'https://techcrunch.com/2019/03/04/the-lonely-death-of-jibo-the-social-robot/', label: 'Read on TechCrunch' },
  },
  {
    year: '2019',
    title: 'How Jibo Succeeded — By Dying',
    description: 'MrMobile (Michael Fisher) released a retrospective exploring how Jibo\'s emotional farewell proved the very thing it set out to demonstrate: that humans can form real bonds with social robots. In failing commercially, Jibo succeeded at its deeper mission.',
    type: 'achievement',
    link: { url: 'https://www.youtube.com/watch?v=25bSlY8JkEA', label: 'Watch on YouTube' },
  },
  {
    year: '2019',
    title: 'Lessons from Social Robot Failures',
    description: 'Cornell roboticist Guy Hoffman published "Anki, Jibo, and Kuri: What We Can Learn from Social Robots That Didn\'t Make It" in IEEE Spectrum, comparing the era to the Apple Newton before smartphones — groundbreaking technology that was too early and too expensive.',
    type: 'milestone',
    link: { url: 'https://spectrum.ieee.org/anki-jibo-and-kuri-what-we-can-learn-from-social-robotics-failures', label: 'Read on IEEE Spectrum' },
  },
  {
    year: 'Fall 2019',
    title: 'MIT Deploys 18 Jibos to Classrooms',
    description: 'MIT\'s Personal Robots Group sent 18 Jibo robots to kindergarten classrooms in underserved Atlanta neighborhoods. The "Personalized Robot Storytelling Companion" project studied whether a social robot peer could improve children\'s linguistic and storytelling skills.',
    type: 'revival',
    link: { url: 'https://www.media.mit.edu/projects/collaborative-robot-storyteller/overview/', label: 'View MIT Project' },
  },
  {
    year: '2019',
    title: 'Hackers Give Jibo New Life',
    description: 'Guilherme Martins replaced Jibo\'s brain with a LattePanda single-board computer, mounted it on an industrial robot arm, and created "Toppi" — a humanized Kit Kat customization robot. Other hackers attempted to reawaken Jibo using a Nintendo Switch exploit targeting its shared NVIDIA Tegra chipset.',
    type: 'revival',
    link: { url: 'https://hackaday.com/2019/10/17/long-live-jibo-our-adorable-robot-companion/', label: 'Read on Hackaday' },
  },
  {
    year: 'Mar 2020',
    title: 'NTT Disruption Acquires Jibo',
    description: 'NTT Disruption acquired Jibo\'s IP, trademark, and assets, repositioning the robot for B2B use in healthcare and education, integrating Microsoft Azure AI for mood recognition and empathetic patient support.',
    type: 'revival',
    link: { url: 'https://www.therobotreport.com/jibo-social-robot-assets-acquired-ntt-disruption/', label: 'Read on The Robot Report' },
  },
  {
    year: '2021',
    title: 'MIT: Jibo Makes Kids More Creative',
    description: 'MIT researchers published a study in Frontiers in Robotics and AI showing that when Jibo modeled creative behavior during gameplay — asking reflective questions, generating diverse ideas, challenging assumptions — children became measurably more creative.',
    type: 'achievement',
    link: { url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC8475758/', label: 'Read the Study' },
  },
  {
    year: 'Mar 2022',
    title: 'Long-Term Robot Companionship Study',
    description: 'MIT published research at ACM/IEEE HRI 2022 where older adults hosted Jibo for 1–12 months. Participants brought Jibo to birthday parties and family gatherings. The study found proactive robot behavior and persona were key to long-term engagement.',
    type: 'achievement',
  },
  {
    year: '2023',
    title: 'NTT Disruption Closes',
    description: 'NTT Disruption itself was shut down, ending Jibo\'s commercial life for a second time. The Jibo research platform continues at MIT.',
    type: 'decline',
  },
  {
    year: 'Mar 2024',
    title: 'Jibo Research Platform Goes @Scale',
    description: 'MIT published "Jibo Community Social Robot Research Platform @Scale" at ACM/IEEE HRI 2024 and hosted a full-day workshop in Boulder, CO. Participants received Jibo robots and built LLM-powered interactions using a new Jibo-ROS framework.',
    type: 'achievement',
    link: { url: 'https://dl.acm.org/doi/10.1145/3610978.3638171', label: 'Read the Paper' },
  },
  {
    year: 'Nov 2024',
    title: 'The JIBO Kids Corpus Published',
    description: 'MIT released the first publicly available English-language child speech dataset with a longitudinal component — 110 children aged 4–7 interacting with Jibo across 21 hours of classroom recordings collected over two years.',
    type: 'achievement',
    link: { url: 'https://pubs.aip.org/asa/jel/article/4/11/115201/3318582/The-JIBO-Kids-Corpus-A-speech-dataset-of-child', label: 'Read the Paper' },
  },
  {
    year: '2025',
    title: 'Jibo Lives On at MIT Museum',
    description: 'Jibo is on display at the MIT Museum\'s "AI: Mind the Gap" exhibition, where visitors can still interact with the robot. MIT\'s RAISE initiative, founded by Cynthia Breazeal, has reached over 1 million students in 170 countries with AI education curricula.',
    type: 'revival',
  },
];

const Timeline: React.FC = () => {
  return (
    <section id="timeline" className="timeline">
      <div className="timeline__container">
        <h2 className="section-title">The Jibo Story</h2>
        <p className="section-subtitle">
          From a dream at MIT to a movement that changed how we think about robots
        </p>

        <div className="timeline__track">
          <div className="timeline__line" />
          {events.map((event, index) => (
            <div key={index} className={`timeline__event timeline__event--${event.type} ${index % 2 === 0 ? 'timeline__event--left' : 'timeline__event--right'}`}>
              <div className="timeline__dot" />
              <div className="timeline__content">
                <span className="timeline__year">{event.year}</span>
                <h3>{event.title}</h3>
                <p>{event.description}</p>
                {event.link && (
                  <a
                    className="timeline__link"
                    href={event.link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {event.link.label} &#x2197;
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Timeline;
